import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Stack, Typography, FormControl, InputLabel, Select, MenuItem,
  Alert, AlertTitle, Box, alpha, useTheme,
  List, ListItem, ListItemText, Divider,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper, Chip,
  IconButton
} from '@mui/material';
import {
  UploadFile, Download, InsertDriveFile, Close
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { SessionClass, SectionInstance, Session } from '../../types/sessions';
import { sectionInstancesApi } from '../../services/sessions/sectionInstancesApi';
import { studentsApi } from '../../services/sessions/studentsApi';
import settingsService from '../../services/settingsService';
import { Group } from '../../types';
import { formatFileSize } from '../../utils/fileSize';

interface BulkEnrollmentImportDialogProps {
  open: boolean;
  activeSession: Session | null;
  defaultClassId?: string;
  defaultSectionInstanceId?: string | null;
  classes: SessionClass[];
  onClose: () => void;
  onImported: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// CSV parser — inline, no new dependency. Handles quoted fields and "" escapes.
// ─────────────────────────────────────────────────────────────────────────────

const splitCsvLine = (line: string): string[] => {
  const cells: string[] = [];
  let cur = ''; let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
      else if (ch === '"') { inQuotes = false; }
      else { cur += ch; }
    } else {
      if (ch === ',') { cells.push(cur.trim()); cur = ''; }
      else if (ch === '"' && cur === '') { inQuotes = true; }
      else { cur += ch; }
    }
  }
  cells.push(cur.trim());
  return cells;
};

const isHeaderLikely = (cells: string[]): boolean => {
  const first = (cells[0] ?? '').toLowerCase();
  return ['name', 'father', 'roll'].some(k => first.includes(k));
};

const TEMPLATE_CSV = [
  '# Bulk add new students — name / fatherName / fatherPhone / phone / address are required.',
  '# rollNumber is optional — leave blank to auto-assign.',
  'name,fatherName,fatherPhone,phone,address,rollNumber',
  'Ali Khan,Khalid Khan,03001234567,03007654321,House #12 Block A,',
  'Sara Ahmed,Imran Ahmed,03219876543,03211234567,Flat 4 Tower B,5',
  'Hassan Raza,Raza Akram,03331112233,03334445566,123 Main Street,'
].join('\n');

interface ParsedRow {
  lineNo: number;
  name: string;
  fatherName: string;
  fatherPhone: string;
  phone: string;
  address: string;
  rollNumber?: number;
}

const parseCSV = (text: string): { rows: ParsedRow[]; parseErrors: string[] } => {
  const rows: ParsedRow[] = [];
  const parseErrors: string[] = [];
  const lines = text.replace(/^﻿/, '').split(/\r?\n/);
  let headerSeen = false;

  lines.forEach((line, idx) => {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#')) return;
    const cells = splitCsvLine(trimmed);
    if (!headerSeen) {
      headerSeen = true;
      if (isHeaderLikely(cells)) return;
    }
    const [name, fatherName, fatherPhone, phone, address, roll] = cells;
    const missing: string[] = [];
    if (!name)        missing.push('name');
    if (!fatherName)  missing.push('fatherName');
    if (!fatherPhone) missing.push('fatherPhone');
    if (!phone)       missing.push('phone');
    if (!address)     missing.push('address');
    if (missing.length) {
      parseErrors.push(`Line ${idx + 1}: missing ${missing.join(', ')}`);
      return;
    }
    let rollNumber: number | undefined;
    if (roll) {
      const n = Number(roll);
      if (!Number.isInteger(n) || n < 1) {
        parseErrors.push(`Line ${idx + 1}: roll "${roll}" must be a positive integer`);
        return;
      }
      rollNumber = n;
    }
    rows.push({ lineNo: idx + 1, name, fatherName, fatherPhone, phone, address, rollNumber });
  });
  return { rows, parseErrors };
};

const downloadTemplate = () => {
  const blob = new Blob([TEMPLATE_CSV], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = 'students-template.csv';
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// ─────────────────────────────────────────────────────────────────────────────

const BulkEnrollmentImportDialog: React.FC<BulkEnrollmentImportDialogProps> = ({
  open, activeSession, defaultClassId, defaultSectionInstanceId, classes, onClose, onImported
}) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [classId, setClassId] = useState<string>(defaultClassId ?? '');
  const [sectionInstanceId, setSectionInstanceId] = useState<string>((defaultSectionInstanceId ?? '') as string);
  const [groupId, setGroupId] = useState<string>('');
  const [groups, setGroups] = useState<Group[]>([]);
  const [sections, setSections] = useState<SectionInstance[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [fileText, setFileText] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [serverErrors, setServerErrors] = useState<Array<{ row: number; message: string }>>([]);

  useEffect(() => {
    if (open) {
      setClassId(defaultClassId ?? '');
      setSectionInstanceId((defaultSectionInstanceId ?? '') as string);
      setGroupId('');
      setFile(null);
      setFileText('');
      setServerErrors([]);
    }
  }, [open, defaultClassId, defaultSectionInstanceId]);

  // Load groups once per open. Same pattern as the single-student dialog.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await settingsService.getGroups({ isActive: true, limit: 200 });
        if (!cancelled) setGroups(res?.data?.groups || []);
      } catch { /* silent — picker shows empty list */ }
    })();
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!open || !activeSession || !classId) { setSections([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const secs = await sectionInstancesApi.list({ sessionId: activeSession._id, classId });
        if (!cancelled) setSections(secs);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [open, activeSession, classId]);

  const handleFileChosen = async (chosen: File | null) => {
    if (!chosen) return;
    setServerErrors([]);
    setFile(chosen);
    try {
      const text = await chosen.text();
      setFileText(text);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not read file', { variant: 'error' });
      setFile(null);
      setFileText('');
    }
  };

  const clearFile = () => {
    setFile(null);
    setFileText('');
    setServerErrors([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const { rows, parseErrors } = useMemo(() => parseCSV(fileText), [fileText]);
  const previewRows = rows.slice(0, 8);
  // Section-required rule (matches BE): if the target class has active
  // sections, admin must pick one before we let the import fire.
  const ready = activeSession && classId && rows.length > 0 && parseErrors.length === 0
    && !(sections.length > 0 && !sectionInstanceId);

  const submit = async () => {
    if (!activeSession || !classId) return;
    setSubmitting(true);
    setServerErrors([]);
    try {
      const result = await studentsApi.bulkCreate({
        sessionId: activeSession._id,
        classId,
        sectionInstanceId: sectionInstanceId || null,
        groupId: groupId || null,
        rows: rows.map(({ lineNo, ...rest }) => rest)
      });
      enqueueSnackbar(
        `${result.created} student${result.created === 1 ? '' : 's'} created and enrolled`,
        { variant: 'success' }
      );
      onImported();
      onClose();
    } catch (err: any) {
      const errors = err?.details?.errors;
      if (Array.isArray(errors)) {
        setServerErrors(errors);
      } else {
        enqueueSnackbar(err?.message || 'Bulk import failed', { variant: 'error' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!activeSession) return null;

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle component="div" sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <UploadFile color="primary" fontSize="small" />
          <Typography variant="h6" fontWeight={600}>Bulk add students</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Upload a CSV to create new student records and enroll them into a class in one go.
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={2.5}>
          {/* Slot picker — Class / Section / Group apply to every row in the CSV */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControl fullWidth>
              <InputLabel id="bulk-class-label">Class</InputLabel>
              <Select
                labelId="bulk-class-label"
                label="Class"
                value={classId}
                onChange={(e) => { setClassId(e.target.value); setSectionInstanceId(''); }}
              >
                {classes.map(c => <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl
              fullWidth
              required={sections.length > 0}
              disabled={!classId || sections.length === 0}
            >
              <InputLabel id="bulk-section-label">Section</InputLabel>
              <Select
                labelId="bulk-section-label"
                label="Section"
                value={sectionInstanceId}
                onChange={(e) => setSectionInstanceId(e.target.value)}
              >
                {sections.length === 0 ? (
                  <MenuItem value=""><em>No sections in this class</em></MenuItem>
                ) : (
                  <MenuItem value="" disabled><em>Select a section</em></MenuItem>
                )}
                {sections.map(s => <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="bulk-group-label">Group (optional)</InputLabel>
              <Select
                labelId="bulk-group-label"
                label="Group (optional)"
                value={groupId}
                onChange={(e) => setGroupId(e.target.value)}
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {groups.map(g => <MenuItem key={g._id} value={g._id}>{g.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>

          {/* Step 1 — template download */}
          <Box sx={{
            p: 1.5, borderRadius: 1.5,
            bgcolor: alpha(theme.palette.primary.main, 0.04),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`
          }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
              <Box>
                <Typography variant="body2" fontWeight={600}>Need the template?</Typography>
                <Typography variant="caption" color="text.secondary">
                  Columns: <code>name</code> · <code>fatherName</code> · <code>fatherPhone</code> · <code>phone</code> · <code>address</code> (required) + <code>rollNumber</code> (blank = auto).
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Download fontSize="small" />}
                onClick={downloadTemplate}
                sx={{ borderRadius: 2, whiteSpace: 'nowrap' }}
              >
                Download CSV
              </Button>
            </Stack>
          </Box>

          {/* Step 2 — file upload */}
          <Box>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              hidden
              onChange={(e) => handleFileChosen(e.target.files?.[0] ?? null)}
            />

            {!file ? (
              <Box
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  p: 3, borderRadius: 1.5, cursor: 'pointer',
                  border: `1px dashed ${theme.palette.divider}`,
                  textAlign: 'center',
                  transition: 'border-color 120ms ease, background-color 120ms ease',
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    bgcolor: alpha(theme.palette.primary.main, 0.04)
                  }
                }}
              >
                <UploadFile sx={{ fontSize: 28, color: 'text.disabled', mb: 0.5 }} />
                <Typography variant="body2" fontWeight={500}>Click to upload your CSV file</Typography>
                <Typography variant="caption" color="text.secondary">.csv files only</Typography>
              </Box>
            ) : (
              <Box sx={{
                p: 1.5, borderRadius: 1.5,
                border: `1px solid ${theme.palette.divider}`,
                display: 'flex', alignItems: 'center', gap: 1.5
              }}>
                <InsertDriveFile sx={{ color: 'primary.main', fontSize: 22 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>{file.name}</Typography>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                      {formatFileSize(file.size)}
                    </Typography>
                    <Chip
                      size="small"
                      label={`${rows.length} valid row${rows.length === 1 ? '' : 's'}`}
                      color={rows.length > 0 ? 'success' : 'default'}
                      variant="outlined"
                      sx={{ height: 18, fontSize: 11 }}
                    />
                    {parseErrors.length > 0 && (
                      <Chip
                        size="small"
                        label={`${parseErrors.length} parse error${parseErrors.length === 1 ? '' : 's'}`}
                        color="warning"
                        variant="outlined"
                        sx={{ height: 18, fontSize: 11 }}
                      />
                    )}
                  </Stack>
                </Box>
                <IconButton size="small" onClick={clearFile} aria-label="Remove file">
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>

          {/* Preview */}
          {file && rows.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Preview ({previewRows.length} of {rows.length})
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: alpha(theme.palette.text.primary, 0.025) }}>
                      <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: 12 }}>Line</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: 12 }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: 12 }}>Father</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: 12 }}>Phone</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: 12 }}>Roll #</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewRows.map((r, idx) => (
                      <TableRow key={`${r.lineNo}-${idx}`}>
                        <TableCell sx={{ color: 'text.disabled' }}>{r.lineNo}</TableCell>
                        <TableCell><Typography variant="body2" fontWeight={500}>{r.name}</Typography></TableCell>
                        <TableCell><Typography variant="body2">{r.fatherName}</Typography></TableCell>
                        <TableCell><Typography variant="body2">{r.phone}</Typography></TableCell>
                        <TableCell>
                          {r.rollNumber ?? <Typography variant="caption" color="text.disabled">auto</Typography>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {rows.length > previewRows.length && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  + {rows.length - previewRows.length} more row{rows.length - previewRows.length === 1 ? '' : 's'}
                </Typography>
              )}
            </Box>
          )}

          {/* Parse errors */}
          {parseErrors.length > 0 && (
            <Alert severity="warning" variant="outlined">
              <AlertTitle sx={{ fontWeight: 600 }}>Fix these lines first</AlertTitle>
              <List dense disablePadding>
                {parseErrors.slice(0, 10).map((e, i) => (
                  <ListItem key={i} sx={{ py: 0 }}>
                    <ListItemText primary={e} primaryTypographyProps={{ variant: 'body2' }} />
                  </ListItem>
                ))}
              </List>
              {parseErrors.length > 10 && (
                <Typography variant="caption" color="text.secondary">+ {parseErrors.length - 10} more</Typography>
              )}
            </Alert>
          )}

          {/* Server errors */}
          {serverErrors.length > 0 && (
            <Alert severity="error" variant="outlined">
              <AlertTitle sx={{ fontWeight: 600 }}>The whole batch was rejected — fix and retry</AlertTitle>
              <List dense disablePadding>
                {serverErrors.slice(0, 12).map((e, i) => (
                  <React.Fragment key={i}>
                    <ListItem sx={{ py: 0.25 }}>
                      <ListItemText
                        primary={`Row ${e.row}`}
                        secondary={e.message}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                    {i < Math.min(11, serverErrors.length - 1) && <Divider component="li" />}
                  </React.Fragment>
                ))}
              </List>
              {serverErrors.length > 12 && (
                <Typography variant="caption" color="text.secondary">+ {serverErrors.length - 12} more</Typography>
              )}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          disableElevation
          disabled={!ready || submitting}
          onClick={submit}
          sx={{ borderRadius: 2 }}
        >
          {submitting
            ? 'Creating…'
            : `Create & enroll ${rows.length || ''} student${rows.length === 1 ? '' : 's'}`.replace(/\s+/g, ' ').trim()}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkEnrollmentImportDialog;
